import { Megaphone, Send } from "lucide-react";
import { useMemo, useState } from "react";
import { AdminApi } from "@/api";
import { ProactiveComposerPanel } from "@/components/proactive-messages/ProactiveComposerPanel";
import { ProactiveLogPanel } from "@/components/proactive-messages/ProactiveLogPanel";
import { cn } from "@/lib/utils";

type Tab = "log" | "send";

/**
 * Proactive Messages section.
 *
 * Sending and reviewing are different jobs. This page was originally only the
 * send composer, which is why sent messages could not be reviewed at all —
 * and why the automated beat (the bulk of real traffic) had no surface here.
 * The log is the default tab for that reason.
 *
 * The log reads through the SAM token like every other admin screen, and so
 * does the composer's search; the composer's send keeps its own prod-Supabase
 * login.
 */
export function ProactiveMessagesPage({
  apiBaseUrl,
  bearerToken,
  samsApiBaseUrl,
}: {
  apiBaseUrl: string;
  bearerToken: string;
  samsApiBaseUrl?: string;
}) {
  const [tab, setTab] = useState<Tab>("log");
  const api = useMemo(
    () => AdminApi.forSamsLogin(apiBaseUrl, bearerToken, apiBaseUrl, samsApiBaseUrl || undefined),
    [apiBaseUrl, bearerToken, samsApiBaseUrl],
  );

  // Title and description live in the shell's page header (pageMeta).
  return (
    <div className="grid gap-5">
      {/* No Tabs primitive in this repo — styled buttons, same as the
          composer's direct/compose picker. */}
      <div className="grid gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setTab("log")}
          className={cn(
            "rounded-lg border p-3 text-left",
            tab === "log" ? "border-primary bg-primary/8" : "border-border",
          )}
        >
          <div className="flex items-center gap-2 font-semibold">
            <Megaphone className="size-4" /> 발송 로그
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            누구에게 무엇이 갔고 어떻게 반응했는지
          </div>
        </button>
        <button
          type="button"
          onClick={() => setTab("send")}
          className={cn(
            "rounded-lg border p-3 text-left",
            tab === "send" ? "border-primary bg-primary/8" : "border-border",
          )}
        >
          <div className="flex items-center gap-2 font-semibold">
            <Send className="size-4" /> 보내기
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            운영자가 prod 세션에 직접 발송
          </div>
        </button>
      </div>

      {tab === "log" ? (
        <ProactiveLogPanel api={api} />
      ) : (
        <ProactiveComposerPanel api={api} />
      )}
    </div>
  );
}
